import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  ChevronDown,
  TrendingUp,
  Shield,
  PieChart,
  Target,
  Globe,
  BookOpen,
  HelpCircle,
  Lock,
  MessageSquare,
  Users,
  Briefcase,
  GraduationCap,
  Heart,
  Zap,
  FileText,
  BarChart3,
  Moon,
  Sun,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { CleviaLogo } from '@/components/CleviaLogo';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/contexts/ThemeContext';
import { EASE_OUT } from '@/components/landing/motion-primitives';

/**
 * En-tête des pages publiques.
 * Même direction que la landing : tokens de thème, accent unique, méga-menus
 * sobres qui reprennent la trame de cartes du reste du site. Le mouvement sert
 * à suivre le curseur — surbrillance qui glisse, entrées en cascade — et se
 * coupe entièrement sous `prefers-reduced-motion`.
 */

interface MenuEntry {
  icon: ReactNode;
  label: string;
  description: string;
  href?: string;
  comingSoon?: boolean;
}

interface MenuColumn {
  heading: string;
  items: MenuEntry[];
}

/* ── Chorégraphie d'ouverture ────────────────────────────────────── */

/** Le panneau lui-même : descend et s'efface. */
const PANEL_VARIANTS = {
  hidden: { opacity: 0, y: -10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE_OUT } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.14, ease: 'easeIn' as const } },
};

/** Son contenu : cascade à l'ouverture, sortie courte au changement d'onglet. */
const CONTENT_VARIANTS = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.035, delayChildren: 0.02 } },
  exit: { opacity: 0, transition: { duration: 0.12, ease: 'easeIn' as const } },
};

const ENTRY_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: EASE_OUT } },
};

/* ── Une entrée de méga-menu ─────────────────────────────────────── */

function MenuItem({
  item,
  comingSoonLabel,
  onNavigate,
}: {
  item: MenuEntry;
  comingSoonLabel: string;
  onNavigate: () => void;
}) {
  const content = (
    <>
      {/* Filet d'accent qui se déplie à gauche au survol */}
      <span
        aria-hidden="true"
        className="absolute inset-y-2 left-0 w-0.5 origin-center scale-y-0 rounded-full bg-primary transition-transform duration-300 ease-out group-hover/item:scale-y-100"
      />
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50 text-primary transition-all duration-300 group-hover/item:-translate-y-0.5 group-hover/item:border-primary/40 group-hover/item:bg-primary/10 dark:text-brand-400">
        {item.icon}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-foreground transition-colors duration-200 group-hover/item:text-brand-700 dark:group-hover/item:text-brand-400">
            {item.label}
          </span>
          {item.comingSoon && (
            <span className="shrink-0 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {comingSoonLabel}
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-pretty text-xs leading-relaxed text-muted-foreground">
          {item.description}
        </span>
      </span>

      {item.href && (
        <ArrowRight
          aria-hidden="true"
          className="mt-1 h-3.5 w-3.5 shrink-0 -translate-x-1 text-primary opacity-0 transition-all duration-300 group-hover/item:translate-x-0 group-hover/item:opacity-100"
        />
      )}
    </>
  );

  const className =
    'group/item relative flex items-start gap-3 rounded-lg p-3 pl-4 transition-colors duration-200 hover:bg-muted/60';

  return (
    <motion.div variants={ENTRY_VARIANTS}>
      {item.href?.startsWith('mailto:') ? (
        <a href={item.href} className={className} onClick={onNavigate}>
          {content}
        </a>
      ) : item.href ? (
        <Link to={item.href} className={className} onClick={onNavigate}>
          {content}
        </Link>
      ) : (
        <div className={`${className} cursor-default`}>{content}</div>
      )}
    </motion.div>
  );
}

/* ── Onglet de navigation ────────────────────────────────────────── */

function NavTrigger({
  label,
  open,
  onOpen,
  onToggle,
  buttonRef,
}: {
  label: string;
  open: boolean;
  onOpen: () => void;
  onToggle: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) {
  const reduce = useReducedMotion();

  return (
    <button
      type="button"
      ref={buttonRef}
      onMouseEnter={onOpen}
      onFocus={onOpen}
      onClick={onToggle}
      aria-expanded={open}
      aria-haspopup="true"
      className={`relative flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 ${
        open ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {/* Surbrillance unique qui glisse d'un onglet à l'autre */}
      {open && !reduce && (
        <motion.span
          layoutId="nav-highlight"
          aria-hidden="true"
          className="absolute inset-0 -z-10 rounded-md bg-muted"
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
        />
      )}
      {label}
      <ChevronDown
        className={`h-4 w-4 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

/* ── Panneau : un seul, qui se transforme d'un menu à l'autre ────── */

function MegaPanel({
  menu,
  comingSoonLabel,
  onNavigate,
}: {
  menu: { key: string; label: string; width: string; columns: MenuColumn[] };
  comingSoonLabel: string;
  onNavigate: () => void;
}) {
  const reduce = useReducedMotion();

  return (
    <motion.div
      layout={!reduce}
      transition={{ layout: { duration: 0.32, ease: EASE_OUT } }}
      className={`pointer-events-auto relative isolate max-w-full overflow-hidden rounded-xl border border-border bg-card p-7 shadow-xl shadow-black/5 dark:shadow-black/40 ${menu.width}`}
    >
      {/* Filet lumineux sur le bord haut, comme les cartes de la landing */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent"
      />

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={menu.key}
          variants={reduce ? undefined : CONTENT_VARIANTS}
          initial={reduce ? false : 'hidden'}
          animate={reduce ? undefined : 'show'}
          exit={reduce ? undefined : 'exit'}
        >
          <motion.p
            variants={reduce ? undefined : ENTRY_VARIANTS}
            className="mb-6 text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground"
          >
            {menu.label}
          </motion.p>

          <div className={`grid gap-8 ${menu.columns.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {menu.columns.map((column) => (
              <div key={column.heading}>
                <motion.h4
                  variants={reduce ? undefined : ENTRY_VARIANTS}
                  className="mb-3 px-4 text-xs font-medium text-foreground"
                >
                  {column.heading}
                </motion.h4>
                <div className="space-y-1">
                  {column.items.map((item, i) => (
                    <MenuItem
                      key={i}
                      item={item}
                      comingSoonLabel={comingSoonLabel}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

/* ── En-tête ─────────────────────────────────────────────────────── */

export function LandingHeader() {
  const { t } = useTranslation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const reduce = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const triggers = useRef<Record<string, HTMLButtonElement | null>>({});
  // Position de la pointe du panneau, alignée sur l'onglet ouvert
  const [caretX, setCaretX] = useState<number | null>(null);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Échap referme le panneau ouvert
  useEffect(() => {
    if (!activeMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeMenu]);

  useEffect(() => () => window.clearTimeout(closeTimer.current ?? undefined), []);

  // La pointe suit l'onglet ouvert, y compris au redimensionnement
  useEffect(() => {
    if (!activeMenu) return;

    const place = () => {
      const trigger = triggers.current[activeMenu];
      const row = rowRef.current;
      if (!trigger || !row) return;
      const triggerBox = trigger.getBoundingClientRect();
      const rowBox = row.getBoundingClientRect();
      // `inset-x-6` sur le panneau : on retire ce décalage de 24 px
      setCaretX(triggerBox.left + triggerBox.width / 2 - rowBox.left - 24);
    };

    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [activeMenu]);

  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openMenu = (key: string) => {
    cancelClose();
    setActiveMenu(key);
  };

  /** Fermeture différée : laisse le temps d'atteindre le panneau en diagonale. */
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setActiveMenu(null), 140);
  };

  const closeNow = () => {
    cancelClose();
    setActiveMenu(null);
  };

  const comingSoonLabel = t('common.comingSoon' as any);

  const featuresColumns = [
    {
      heading: t('header.features.daily' as any),
      items: [
        {
          icon: <PieChart className="h-4 w-4" />,
          label: t('header.features.budget.title' as any),
          description: t('header.features.budget.desc' as any),
        },
        {
          icon: <TrendingUp className="h-4 w-4" />,
          label: t('header.features.transactions.title' as any),
          description: t('header.features.transactions.desc' as any),
        },
        {
          icon: <FileText className="h-4 w-4" />,
          label: t('header.features.calendar.title' as any),
          description: t('header.features.calendar.desc' as any),
        },
        {
          icon: <Globe className="h-4 w-4" />,
          label: t('header.features.multiCurrency.title' as any),
          description: t('header.features.multiCurrency.desc' as any),
        },
      ],
    },
    {
      heading: t('header.features.vision' as any),
      items: [
        {
          icon: <TrendingUp className="h-4 w-4" />,
          label: t('header.features.wealth.title' as any),
          description: t('header.features.wealth.desc' as any),
        },
        {
          icon: <Globe className="h-4 w-4" />,
          label: t('header.features.conversion.title' as any),
          description: t('header.features.conversion.desc' as any),
        },
        {
          icon: <BarChart3 className="h-4 w-4" />,
          label: t('header.features.cashflow.title' as any),
          description: t('header.features.cashflow.desc' as any),
        },
        {
          icon: <Zap className="h-4 w-4" />,
          label: t('header.features.topspending.title' as any),
          description: t('header.features.topspending.desc' as any),
        },
        {
          icon: <TrendingUp className="h-4 w-4" />,
          label: t('header.features.analysis.title' as any),
          description: t('header.features.analysis.desc' as any),
        },
      ],
    },
    {
      heading: t('header.features.strategy' as any),
      items: [
        {
          icon: <Target className="h-4 w-4" />,
          label: t('header.features.goals.title' as any),
          description: t('header.features.goals.desc' as any),
        },
        {
          icon: <Target className="h-4 w-4" />,
          label: t('header.features.allocation.title' as any),
          description: t('header.features.allocation.desc' as any),
        },
        {
          icon: <Shield className="h-4 w-4" />,
          label: t('header.features.debts.title' as any),
          description: t('header.features.debts.desc' as any),
        },
      ],
    },
  ];

  const solutionsColumns = [
    {
      heading: t('header.solutions.particulars' as any),
      items: [
        {
          icon: <GraduationCap className="h-4 w-4" />,
          label: t('header.solutions.students.title' as any),
          description: t('header.solutions.students.desc' as any),
        },
        {
          icon: <Zap className="h-4 w-4" />,
          label: t('header.solutions.young.title' as any),
          description: t('header.solutions.young.desc' as any),
        },
        {
          icon: <Globe className="h-4 w-4" />,
          label: t('header.solutions.mre.title' as any),
          description: t('header.solutions.mre.desc' as any),
        },
        {
          icon: <Heart className="h-4 w-4" />,
          label: t('header.solutions.couples.title' as any),
          description: t('header.solutions.couples.desc' as any),
          comingSoon: true,
        },
      ],
    },
    {
      heading: t('header.solutions.professionals' as any),
      items: [
        {
          icon: <Briefcase className="h-4 w-4" />,
          label: t('header.solutions.freelancers.title' as any),
          description: t('header.solutions.freelancers.desc' as any),
        },
        {
          icon: <Heart className="h-4 w-4" />,
          label: t('header.solutions.coaches.title' as any),
          description: t('header.solutions.coaches.desc' as any),
          comingSoon: true,
        },
      ],
    },
  ];

  const resourcesColumns = [
    {
      heading: t('header.resources.learn' as any),
      items: [
        {
          icon: <BookOpen className="h-4 w-4" />,
          label: t('header.resources.guide.title' as any),
          description: t('header.resources.guide.desc' as any),
          href: '/help',
        },
        {
          icon: <BookOpen className="h-4 w-4" />,
          label: t('header.resources.blog.title' as any),
          description: t('header.resources.blog.desc' as any),
          href: '/blog',
        },
      ],
    },
    {
      heading: t('header.resources.support' as any),
      items: [
        {
          icon: <HelpCircle className="h-4 w-4" />,
          label: t('header.resources.help.title' as any),
          description: t('header.resources.help.desc' as any),
          href: '/help',
        },
        {
          icon: <Lock className="h-4 w-4" />,
          label: t('header.resources.security.title' as any),
          description: t('header.resources.security.desc' as any),
          href: '/p/privacy',
        },
        {
          icon: <MessageSquare className="h-4 w-4" />,
          label: t('header.resources.contact.title' as any),
          description: t('header.resources.contact.desc' as any),
          href: 'mailto:founders@cleviaapp.com',
        },
      ],
    },
  ];

  const companyColumns = [
    {
      heading: 'clevia',
      items: [
        {
          icon: <Users className="h-4 w-4" />,
          label: t('header.company.founders.title' as any),
          description: t('header.company.founders.desc' as any),
          href: '/founders',
        },
      ],
    },
    {
      heading: t('header.company.transparency' as any),
      items: [
        {
          icon: <Zap className="h-4 w-4" />,
          label: t('header.company.press.title' as any),
          description: t('header.company.press.desc' as any),
          href: '/press',
        },
        {
          icon: <FileText className="h-4 w-4" />,
          label: t('header.company.legal.title' as any),
          description: t('header.company.legal.desc' as any),
          href: '/p/legal',
        },
        {
          icon: <Lock className="h-4 w-4" />,
          label: t('header.company.privacy.title' as any),
          description: t('header.company.privacy.desc' as any),
          href: '/p/privacy',
        },
        {
          icon: <MessageSquare className="h-4 w-4" />,
          label: t('header.company.contact.title' as any),
          description: t('header.company.contact.desc' as any),
          href: 'mailto:founders@cleviaapp.com',
        },
      ],
    },
  ];

  const menus: { key: string; label: string; width: string; columns: MenuColumn[] }[] = [
    {
      key: 'features',
      label: t('header.features' as any),
      width: 'w-[900px]',
      columns: featuresColumns,
    },
    {
      key: 'solutions',
      label: t('header.solutions' as any),
      width: 'w-[760px]',
      columns: solutionsColumns,
    },
    {
      key: 'resources',
      label: t('header.resources' as any),
      width: 'w-[760px]',
      columns: resourcesColumns,
    },
    {
      key: 'company',
      label: t('header.company' as any),
      width: 'w-[760px]',
      columns: companyColumns,
    },
  ];

  const openMenuData = menus.find((menu) => menu.key === activeMenu);

  return (
    <nav
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'border-b border-border bg-background/85 py-3 backdrop-blur-xl' : 'py-5'
      }`}
    >
      <div
        ref={rowRef}
        className="relative mx-auto flex max-w-6xl items-center justify-between gap-6 px-6"
        onMouseLeave={scheduleClose}
      >
        <Link to="/" className="transition-opacity duration-200 hover:opacity-80">
          <CleviaLogo size="sm" label="clevia App" />
        </Link>

        {/* Onglets */}
        <div className="hidden items-center gap-1 lg:flex">
          {menus.map((menu) => (
            <NavTrigger
              key={menu.key}
              label={menu.label}
              open={activeMenu === menu.key}
              onOpen={() => openMenu(menu.key)}
              onToggle={() => (activeMenu === menu.key ? closeNow() : openMenu(menu.key))}
              buttonRef={(el) => {
                triggers.current[menu.key] = el;
              }}
            />
          ))}
        </div>

        {/* Panneau unique, ancré sur la largeur de l'en-tête */}
        <AnimatePresence>
          {openMenuData && (
            <motion.div
              variants={PANEL_VARIANTS}
              initial="hidden"
              animate="show"
              exit="exit"
              onMouseEnter={cancelClose}
              className="pointer-events-none absolute inset-x-6 top-full z-50 hidden justify-center pt-3 lg:flex"
            >
              <MegaPanel
                menu={openMenuData}
                comingSoonLabel={comingSoonLabel}
                onNavigate={closeNow}
              />

              {/* Pointe qui glisse jusqu'à l'onglet ouvert */}
              {caretX !== null && (
                <motion.span
                  aria-hidden="true"
                  initial={false}
                  animate={{ x: caretX - 5 }}
                  transition={
                    reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 34 }
                  }
                  className="absolute left-0 top-3 h-2.5 w-2.5 -translate-y-1/2 rotate-45 rounded-[2px] border-l border-t border-border bg-card"
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Préférences + accès compte */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleDarkMode}
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <LanguageSwitcher />

          <Link to="/login">
            <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
              {t('nav.login' as any)}
            </Button>
          </Link>

          <Link to="/register">
            <Button className="shadow-lg shadow-brand-600/20 transition-transform duration-200 hover:-translate-y-0.5">
              {t('nav.register' as any)}
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}
